<template>
  <div class="programDetailPage" id="programDetailPage" ref="programDetailPage">
    <TopBack :title="$t('lang.programDetail.title')"/>

    <div class="page-body">
      <!-- 促销条 -->
      <div class="promo-banner">
        {{ $t('lang.programDetail.promoPrefix') }} <span class="promo-strong">{{ $t('lang.programDetail.promoAmount') }}</span> {{ $t('lang.programDetail.promoSuffix') }}
      </div>

      <!-- 进行中周期卡 -->
      <section class="card cycle-card">
        <span class="badge badge-cycle">{{ $t('lang.programDetail.activeCycle') }}</span>
        <h2 class="cycle-title">{{ $t(cycle.titleKey) }}</h2>
        <p class="cycle-sub">{{ $t('lang.programDetail.cycleSubtitle') }}</p>
      </section>

      <!-- How It Works -->
      <section class="card how-card">
        <div class="how-head">
          <h3 class="card-title">{{ $t('lang.programDetail.howItWorks') }}</h3>
          <span class="badge badge-steps">{{ $t('lang.programDetail.simpleSteps') }}</span>
        </div>
        <div class="steps-panel">
          <template v-for="(s, i) in steps">
            <div class="step" :key="s.n">
              <div class="step-circle">{{ s.n }}</div>
              <p class="step-label">{{ $t(s.labelKey) }}</p>
            </div>
            <div class="step-divider" v-if="i < steps.length - 1" :key="'d' + i"></div>
          </template>
        </div>
        <div class="note-row">
          <svg viewBox="0 0 24 24" class="ic-clock" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <span class="note-text">{{ $t('lang.programDetail.noteShared') }}</span>
        </div>
      </section>

      <!-- Program Specifications -->
      <section class="card spec-card">
        <h3 class="card-title">{{ $t('lang.programDetail.specifications') }}</h3>

        <div class="spec-row">
          <span class="spec-label">{{ $t('lang.programDetail.deposit') }}</span>
          <span class="spec-value">{{ separateThousandInteger(spec.deposit) }} {{ $t('lang.account.currencyType') }}</span>
        </div>
        <div class="spec-divider"></div>

        <div class="spec-row">
          <span class="spec-label">{{ $t('lang.programDetail.duration') }}</span>
          <span class="spec-value">{{ $t('lang.programDetail.durationValue') }}</span>
        </div>
        <div class="spec-divider"></div>

        <div class="spec-row">
          <span class="spec-label">{{ $t('lang.programDetail.dailyTarget') }}</span>
          <span class="spec-value">{{ $t('lang.programDetail.dailyTargetValue') }}</span>
        </div>
        <div class="spec-divider"></div>

        <div class="spec-row">
          <span class="spec-label">{{ $t('lang.programDetail.rewardFormula') }}</span>
          <div class="spec-value-col">
            <span class="spec-value brand">{{ $t('lang.programDetail.upToBc', {amount: separateThousandInteger(spec.rewardMax)}) }}</span>
            <span class="spec-sub brand">{{ $t('lang.programDetail.prorated') }}</span>
          </div>
        </div>
        <div class="spec-divider"></div>

        <div class="spec-row">
          <span class="spec-label spec-label--wrap">{{ $t('lang.programDetail.principalProtection') }}</span>
          <span class="pill-protect">
            <svg viewBox="0 0 24 24" class="ic-check" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"/></svg>
            {{ $t('lang.programDetail.depositReturned') }}
          </span>
        </div>
        <div class="spec-divider"></div>

        <div class="spec-row">
          <span class="spec-label">{{ $t('lang.programDetail.settlement') }}</span>
          <span class="spec-value spec-value--note">{{ $t('lang.programDetail.settlementValue') }}</span>
        </div>
      </section>

      <!-- 结算保障 -->
      <section class="guarantee-card">
        <div class="shield-badge">
          <svg viewBox="0 0 24 24" class="ic-shield" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>
        </div>
        <div class="guarantee-text">
          <h4 class="guarantee-title">{{ $t('lang.programDetail.guaranteeTitle') }}</h4>
          <p class="guarantee-desc">{{ $t('lang.programDetail.guaranteeDesc') }}</p>
        </div>
      </section>
    </div>

    <!-- 底部固定操作栏 -->
    <div class="bottom-bar">
      <div class="bb-left">
        <div class="bb-required-row">
          <span class="bb-required">{{ $t('lang.programDetail.required') }}</span>
          <span class="badge badge-discount">{{ $t('lang.programDetail.discountApplied') }}</span>
        </div>
        <div class="bb-deposit">{{ $t('lang.programDetail.deposit') }} <span class="brand">{{ separateThousandInteger(payAmount) }} {{ $t('lang.account.currencyType') }}</span></div>
      </div>
      <button class="bb-start" type="button" @click="onStart">{{ $t('lang.programDetail.start') }}</button>
    </div>
  </div>
</template>

<script>
import {separateThousandInteger} from '@/utils'
import TopBack from '@/components/TopBack/index'

export default {
  name: 'ProgramDetail',
  components: {TopBack},
  data () {
    return {
      // 静态占位数据：文案走 i18n（titleKey/labelKey + programDetail.* 值），数值为基准屏 D1 (857ec62a)。
      // 后端 Program Detail 接口尚未提供，service/service2 不在本次改动范围；接口就绪后由路由 query 带入 program key 并请求填充。
      cycle: {titleKey: 'lang.training.programTitles.p7d'},
      steps: [
        {n: 1, labelKey: 'lang.programDetail.step1'},
        {n: 2, labelKey: 'lang.programDetail.step2'},
        {n: 3, labelKey: 'lang.programDetail.step3'}
      ],
      spec: {
        deposit: 20000,
        rewardMax: 1400
      },
      payAmount: 10000
    }
  },
  methods: {
    separateThousandInteger,
    onStart () {
      // TODO: 提交参与 program（扣款 / 开通训练周期）
    }
  }
}
</script>

<style lang='less' scoped>
.programDetailPage {
  position: relative;
  min-height: 100dvh;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  background: var(--stitch-page-bg);
}

.page-body {
  padding: calc(60vw / 3.6) calc(16vw / 3.6) calc(100vw / 3.6); /* 顶部 TopBack，底部让开固定栏 */
  display: flex;
  flex-direction: column;
  gap: calc(12vw / 3.6);
}

/* ---- 通用卡片 ---- */
.card {
  background: var(--stitch-surface);
  border: calc(1vw / 3.6) solid var(--stitch-card-border-soft);
  border-radius: var(--stitch-radius-xl);
  padding: calc(14vw / 3.6);
}
.card-title { font-size: calc(15vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); }
.badge { background: var(--stitch-promo-bg); color: var(--stitch-brand); font-weight: 700; font-size: calc(10vw / 3.6); border-radius: calc(4vw / 3.6); line-height: 1; }

/* ---- 促销条 ---- */
.promo-banner { background: var(--stitch-promo-bg); border: calc(1vw / 3.6) solid var(--stitch-promo-border); border-radius: var(--stitch-radius-md); padding: calc(10vw / 3.6) calc(14vw / 3.6); font-size: calc(13vw / 3.6); line-height: calc(20vw / 3.6); color: #334155; }
.promo-strong { color: var(--stitch-brand); font-weight: 700; }

/* ---- 周期卡 ---- */
.cycle-card { display: flex; flex-direction: column; align-items: flex-start; gap: calc(6vw / 3.6); }
.badge-cycle { padding: calc(4vw / 3.6) calc(8vw / 3.6); letter-spacing: 0.5px; }
.cycle-title { font-size: calc(17vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); line-height: calc(22vw / 3.6); }
.cycle-sub { font-size: calc(12vw / 3.6); color: var(--stitch-text-secondary); line-height: calc(16vw / 3.6); }

/* ---- How It Works ---- */
.how-card { display: flex; flex-direction: column; gap: calc(12vw / 3.6); }
.how-head { display: flex; align-items: center; justify-content: space-between; }
.badge-steps { padding: calc(4vw / 3.6) calc(8vw / 3.6); }
.steps-panel { background: var(--stitch-steps-panel); border: calc(1vw / 3.6) solid var(--stitch-card-border); border-radius: var(--stitch-radius-lg); padding: calc(12vw / 3.6) calc(8vw / 3.6); display: flex; align-items: flex-start; justify-content: space-between; }
.step { flex: 1; display: flex; flex-direction: column; align-items: center; gap: calc(6vw / 3.6); }
.step-circle { width: calc(20vw / 3.6); height: calc(20vw / 3.6); border-radius: var(--stitch-radius-full); background: var(--stitch-step-circle); color: var(--stitch-brand); font-size: calc(11vw / 3.6); font-weight: 700; display: flex; align-items: center; justify-content: center; }
.step-label { font-size: calc(11vw / 3.6); font-weight: 500; line-height: calc(14vw / 3.6); color: #1E293B; text-align: center; }
.step-divider { width: calc(1vw / 3.6); height: calc(36vw / 3.6); background: var(--stitch-track); flex-shrink: 0; }
.note-row { display: flex; align-items: center; gap: calc(6vw / 3.6); }
.ic-clock { width: calc(13vw / 3.6); height: calc(13vw / 3.6); color: var(--stitch-text-weak); flex-shrink: 0; }
.note-text { font-size: calc(11vw / 3.6); color: var(--stitch-text-secondary); }

/* ---- Specifications ---- */
.spec-card { display: flex; flex-direction: column; }
.spec-row { display: flex; align-items: center; justify-content: space-between; padding: calc(11vw / 3.6) 0; }
.spec-label { font-size: calc(13vw / 3.6); color: var(--stitch-text-secondary); }
.spec-label--wrap { width: calc(120vw / 3.6); line-height: calc(16vw / 3.6); }
.spec-value { font-size: calc(14vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); text-align: right; }
.spec-value.brand { color: var(--stitch-brand); font-size: calc(14vw / 3.6); }
.spec-value--note { font-size: calc(11vw / 3.6); font-weight: 500; max-width: calc(200vw / 3.6); line-height: calc(15vw / 3.6); }
.spec-value-col { display: flex; flex-direction: column; align-items: flex-end; }
.spec-sub { font-size: calc(11vw / 3.6); color: var(--stitch-brand); }
.spec-divider { height: calc(1vw / 3.6); background: var(--stitch-card-border); }
.pill-protect { display: inline-flex; align-items: center; gap: calc(6vw / 3.6); background: var(--stitch-protection-bg); border: calc(1vw / 3.6) solid var(--stitch-protection-border); color: var(--stitch-brand); font-size: calc(11vw / 3.6); font-weight: 600; padding: calc(5vw / 3.6) calc(10vw / 3.6); border-radius: calc(4vw / 3.6); }
.ic-check { width: calc(12vw / 3.6); height: calc(12vw / 3.6); }

/* ---- 结算保障 ---- */
.guarantee-card { background: var(--stitch-rewards-bg); border: calc(1vw / 3.6) solid var(--stitch-guarantee-border); border-radius: var(--stitch-radius-xl); padding: calc(14vw / 3.6); display: flex; align-items: flex-start; gap: calc(10vw / 3.6); }
.shield-badge { width: calc(34vw / 3.6); height: calc(34vw / 3.6); border-radius: var(--stitch-radius-full); background: var(--stitch-shield-badge); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: var(--stitch-brand); }
.ic-shield { width: calc(16vw / 3.6); height: calc(16vw / 3.6); }
.guarantee-text { display: flex; flex-direction: column; gap: calc(4vw / 3.6); }
.guarantee-title { font-size: calc(13vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); }
.guarantee-desc { font-size: calc(11vw / 3.6); color: var(--stitch-text-mid); line-height: calc(15vw / 3.6); }

/* ---- 底部固定栏 ---- */
.bottom-bar { position: fixed; left: 0; bottom: 0; width: 100vw; min-height: calc(84vw / 3.6); background: var(--stitch-surface); border-top: calc(1vw / 3.6) solid var(--stitch-track); box-shadow: 0 calc(-4vw / 3.6) calc(12vw / 3.6) rgba(0, 0, 0, .03); display: flex; align-items: center; justify-content: space-between; padding: calc(12vw / 3.6) calc(16vw / 3.6); z-index: 99999; }
.bb-left { display: flex; flex-direction: column; gap: calc(4vw / 3.6); }
.bb-required-row { display: flex; align-items: center; gap: calc(8vw / 3.6); }
.bb-required { font-size: calc(10vw / 3.6); font-weight: 700; letter-spacing: 0.05em; color: var(--stitch-text-weak); }
.badge-discount { padding: calc(3vw / 3.6) calc(6vw / 3.6); }
.bb-deposit { font-size: calc(18vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); }
.bb-deposit .brand { color: var(--stitch-brand); }
.bb-start { width: calc(94vw / 3.6); height: calc(44vw / 3.6); background: var(--stitch-brand); color: var(--stitch-surface); font-weight: 700; font-size: calc(14vw / 3.6); border: none; border-radius: var(--stitch-radius-lg); box-shadow: 0 calc(2vw / 3.6) calc(4vw / 3.6) rgba(0, 81, 223, .2); cursor: pointer; }
.bb-start:active { background: var(--stitch-brand-active); }
</style>



// WEBPACK FOOTER //
// src/pages/ProgramDetail/index.vue